/**
 * Sak yant tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sakYantTattoosPack: ToilePack = {
    slug: 'sak-yant-tattoos',
    category: 'tattoo',
    name: 'Sak yant tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'gaoyord', label: 'Gao Yord', subject: 'the Gao Yord, nine spires rising side by side above a row of three small Buddha shapes, rows of Khom script between them'},
        {key: 'hahtaew', label: 'Hah Taew', subject: 'the Hah Taew, five gently curving rows of Khom script stacked one above the other'},
        {key: 'sueakoo', label: 'Suea Koo', subject: 'the Suea Koo, two tigers standing face to face on their hind legs, rows of Khom script in a band beneath them'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'blackwork', label: 'Blackwork', style: 'a blackwork tattoo drawing, heavy solid black masses with the details cut out of them as bare paper', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['Hanuman', 'a lion', 'upper back'],
    directionHint: 'try: Hanuman',
    seo: {
        title: 'Sak yant designs: Gao Yord, Hah Taew, Suea Koo',
        metaDescription: 'Nine sak yant reference designs from Thailand: the Gao Yord, the Hah Taew and the Suea Koo, in three styles, drawn on paper.',
        h1: 'Sak yant designs: Gao Yord, Hah Taew, Suea Koo',
        tagline: 'Three sacred Thai yantra designs, each drawn in fine line, blackwork and dotwork on white paper.',
        intro: 'Sak yant are the sacred tattoos of Thailand, drawn from yantra patterns lined with Khom script and traditionally given by a monk or an ajarn. These sheets are reference drawings of three: the Gao Yord, the Hah Taew and the Suea Koo. Each is drawn as fine line, blackwork and dotwork. A sak yant is given by a teacher; these drawings are for study and for talk with an artist.',
        useCases: [
            'a reference sheet for study of Thai yantra',
            'a discussion piece to take to a sak yant teacher',
            'a printed sheet of the three designs',
            'a comparison of three yantra in three styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine reference drawings of three sak yant: the Gao Yord, the Hah Taew and the Suea Koo, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for study or printing.'},
            {q: 'Can I ask for a different yant or a twist?', a: 'Yes. Type a different yant or twist, such as a lion; the result is a new drawing, not a guaranteed match, and it is not a yant given by a teacher.'},
            {q: 'What is the difference between the styles?', a: 'Fine line is one uniform hairline with little shading. Blackwork uses heavy solid black masses. Dotwork builds the shadows from dots.'},
        ],
    },
}
